import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { UserWithRole } from '@/pages/admin/types';
import { Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface DeleteUserDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  user: UserWithRole | null;
  onConfirmDelete: (userId: string, force: boolean) => Promise<boolean>;
  isDeleting: boolean;
}

interface DeletionBlockers {
  has_certificates: boolean;
  certificate_count: number;
  has_active_assignments: boolean;
  active_assignment_count: number;
  is_protected_admin: boolean;
  can_delete: boolean;
  requires_force: boolean;
}

interface UserDataSummary {
  certificates: number;
  course_assignments: number;
  user_module_progress: number;
  module_attempts: number;
  total_records: number;
}

export const DeleteUserDialog = ({ 
  isOpen, 
  setIsOpen, 
  user, 
  onConfirmDelete,
  isDeleting 
}: DeleteUserDialogProps) => {
  const [confirmText, setConfirmText] = useState('');
  const [forceDelete, setForceDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blockers, setBlockers] = useState<DeletionBlockers | null>(null);
  const [dataSummary, setDataSummary] = useState<UserDataSummary | null>(null);
  const { toast } = useToast();
  
  const canDelete = confirmText === 'DELETE' && (!blockers?.requires_force || forceDelete) && !blockers?.is_protected_admin;

  useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
      setForceDelete(false);
      setBlockers(null);
      setDataSummary(null);
    } else if (user) {
      loadDeletionInfo();
    }
  }, [isOpen, user]);

  const loadDeletionInfo = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Get deletion blockers
      const { data: blockersData, error: blockersError } = await supabase
        .rpc('get_user_deletion_blockers' as any, { target_user_id: user.id }) as any;

      if (blockersError) throw blockersError;
      setBlockers(blockersData);

      // Get data summary
      const { data: summaryData, error: summaryError } = await supabase
        .rpc('get_user_data_summary' as any, { target_user_id: user.id }) as any;

      if (summaryError) throw summaryError;
      setDataSummary(summaryData);
    } catch (error: any) {
      console.error('Error loading deletion info:', error);
      toast({
        title: "Failed to Load User Data",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !canDelete) return;
    
    const success = await onConfirmDelete(user.id, forceDelete);
    if (success) {
      setIsOpen(false);
      setConfirmText('');
      setForceDelete(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete User Account</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-semibold">This action cannot be undone!</p>
              </AlertDescription>
            </Alert>
            
            <div>
              <p className="text-foreground font-semibold mb-1">Deleting user:</p>
              <p className="font-mono text-sm">{user?.name} ({user?.email})</p>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Analyzing user data...</span>
              </div>
            ) : dataSummary && (
              <div className="space-y-2">
                <p className="font-semibold text-foreground">Data to be deleted:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {dataSummary.certificates > 0 && (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{dataSummary.certificates}</Badge>
                      <span>Certificates</span>
                    </div>
                  )}
                  {dataSummary.course_assignments > 0 && (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{dataSummary.course_assignments}</Badge>
                      <span>Assignments</span>
                    </div>
                  )}
                  {dataSummary.user_module_progress > 0 && (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{dataSummary.user_module_progress}</Badge>
                      <span>Module Progress</span>
                    </div>
                  )}
                  {dataSummary.module_attempts > 0 && (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{dataSummary.module_attempts}</Badge>
                      <span>Quiz Attempts</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Total records to delete: <strong>{dataSummary.total_records}</strong>
                </p>
              </div>
            )}

            {blockers?.is_protected_admin && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This user is a protected admin and cannot be deleted.
                </AlertDescription>
              </Alert>
            )}

            {blockers?.requires_force && !blockers.is_protected_admin && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="space-y-2">
                  <p className="font-semibold">Warning: User has active data</p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {blockers.has_certificates && (
                      <li>{blockers.certificate_count} issued certificate(s)</li>
                    )}
                    {blockers.has_active_assignments && (
                      <li>{blockers.active_assignment_count} active course assignment(s)</li>
                    )}
                  </ul>
                  <div className="flex items-center space-x-2 mt-2">
                    <Checkbox 
                      id="force-delete"
                      checked={forceDelete}
                      onCheckedChange={(checked) => setForceDelete(checked as boolean)}
                    />
                    <label
                      htmlFor="force-delete"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Force delete despite active data
                    </label>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div>
              <Label htmlFor="confirm-delete" className="text-foreground">
                Type <strong>DELETE</strong> to confirm
              </Label>
              <Input
                id="confirm-delete"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="mt-2"
                disabled={isDeleting || loading}
              />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={!canDelete || isDeleting || loading}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Deleting...
              </>
            ) : 'Delete User'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteUserDialog;
